import React, { useEffect, useRef, useCallback } from "react";

/* ─────────────────────────────────────────────────────────────────
   3D TILT CARD
   Wraps any element with mouse-tracked perspective tilt + glow
───────────────────────────────────────────────────────────────── */
export function TiltCard({ children, style, className, intensity = 10, glowColor = "#c9a84c" }) {
  const ref = useRef(null);
  const onMove = useCallback((e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const dx = (e.clientX - r.left - r.width  / 2) / (r.width  / 2);
    const dy = (e.clientY - r.top  - r.height / 2) / (r.height / 2);
    el.style.transform = `perspective(900px) rotateY(${dx * intensity}deg) rotateX(${-dy * intensity}deg) translateZ(6px)`;
    el.style.boxShadow = `${-dx * 14}px ${dy * 14}px 48px rgba(0,0,0,0.65), 0 0 40px ${glowColor}15`;
  }, [intensity, glowColor]);
  const onLeave = useCallback(() => {
    const el = ref.current; if (!el) return;
    el.style.transform = "perspective(900px) rotateY(0deg) rotateX(0deg) translateZ(0)";
    el.style.boxShadow = "";
  }, []);
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} className={className}
      style={{ transition: "transform 0.18s ease, box-shadow 0.18s ease", transformStyle: "preserve-3d", willChange: "transform", ...style }}>
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   PARTICLE CANVAS  — 3D depth field
───────────────────────────────────────────────────────────────── */
export function ParticleCanvas({ count = 100, colors = ["#f5c518","#c9a84c","#ffffff"], opacity = 0.85 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let id;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const particles = Array.from({ length: count }, () => ({
      x: (Math.random() - 0.5) * 1800, y: (Math.random() - 0.5) * 1000,
      z: Math.random() * 1200 + 100,
      vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
      vz: Math.random() * 0.5 + 0.2,
      color: colors[Math.floor(Math.random() * colors.length)],
    }));
    let mx = 0, my = 0;
    const onMouse = (e) => { mx = e.clientX - canvas.width / 2; my = e.clientY - canvas.height / 2; };
    window.addEventListener("mousemove", onMouse);
    const FOV = 600;
    const proj = (p) => {
      const s = FOV / (FOV + p.z);
      return { sx: p.x * s + canvas.width / 2 + mx * 0.014 * s, sy: p.y * s + canvas.height / 2 + my * 0.014 * s, s };
    };
    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.sort((a, b) => b.z - a.z);
      for (let i = 0; i < particles.length; i++) {
        const pi = proj(particles[i]);
        for (let j = i + 1; j < particles.length; j++) {
          const pj = proj(particles[j]);
          const d = Math.hypot(pi.sx - pj.sx, pi.sy - pj.sy);
          if (d < 110) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / 110) * 0.13 * pi.s})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(pi.sx, pi.sy); ctx.lineTo(pj.sx, pj.sy); ctx.stroke();
          }
        }
      }
      for (const p of particles) {
        const { sx, sy, s } = proj(p);
        if (sx < -60 || sx > canvas.width + 60 || sy < -60 || sy > canvas.height + 60) continue;
        const r = Math.max(0.5, 2.5 * s);
        const a = Math.min(1, s * 1.2) * opacity;
        const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, r * 5);
        const hex = p.color === "#f5c518" ? `245,197,24` : p.color === "#c9a84c" ? `201,168,76` : `255,255,255`;
        g.addColorStop(0, `rgba(${hex},${a * 0.45})`);
        g.addColorStop(1, `rgba(${hex},0)`);
        ctx.beginPath(); ctx.arc(sx, sy, r * 5, 0, Math.PI * 2); ctx.fillStyle = g; ctx.fill();
        ctx.beginPath(); ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${hex},${a})`; ctx.fill();
        p.x += p.vx; p.y += p.vy; p.z -= p.vz;
        if (p.z < 1) { p.z = 1200; p.x = (Math.random() - 0.5) * 1800; p.y = (Math.random() - 0.5) * 1000; }
      }
      id = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(id); window.removeEventListener("resize", resize); window.removeEventListener("mousemove", onMouse); };
  }, [count, colors, opacity]);
  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />;
}

/* ─────────────────────────────────────────────────────────────────
   GRID BG  — perspective grid that fades to edge
───────────────────────────────────────────────────────────────── */
export function GridBg({ color = "#ffffff", opacity = 0.04, size = 72 }) {
  return (
    <>
      <div style={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(${color}${Math.round(opacity * 255).toString(16).padStart(2,"0")} 1px,transparent 1px),linear-gradient(90deg,${color}${Math.round(opacity * 255).toString(16).padStart(2,"0")} 1px,transparent 1px)`, backgroundSize: `${size}px ${size}px`, pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, transparent 30%, #050505 80%)", pointerEvents: "none" }} />
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────
   GLOW ORB  — ambient floating radial glow
───────────────────────────────────────────────────────────────── */
export function GlowOrb({ color = "#c9a84c", size = 600, top, left, right, bottom, opacity = 0.07, speed = 8 }) {
  return (
    <div style={{
      position: "absolute", width: size, height: size, borderRadius: "50%",
      background: `radial-gradient(ellipse, ${color}${Math.round(opacity * 255).toString(16).padStart(2,"0")} 0%, transparent 70%)`,
      top, left, right, bottom, pointerEvents: "none",
      animation: `glowOrb ${speed}s ease-in-out infinite`,
    }} />
  );
}

/* ─────────────────────────────────────────────────────────────────
   FLOATING RING  — decorative circle outline
───────────────────────────────────────────────────────────────── */
export function FloatingRing({ size = 500, top, left, right, bottom, color = "#c9a84c", opacity = 0.06 }) {
  return (
    <div style={{
      position: "absolute", width: size, height: size, borderRadius: "50%",
      border: `1px solid ${color}${Math.round(opacity * 255).toString(16).padStart(2, "0")}`,
      top, left, right, bottom, pointerEvents: "none",
      animation: `floatY ${10 + size / 100}s ease-in-out infinite`,
    }} />
  );
}

/* ─────────────────────────────────────────────────────────────────
   SCAN LINE  — moving horizontal light sweep
───────────────────────────────────────────────────────────────── */
export function ScanLine({ color = "#f5c518", duration = 4 }) {
  return (
    <div style={{
      position: "absolute", left: 0, right: 0, height: 1,
      background: `linear-gradient(90deg, transparent, ${color}40, transparent)`,
      animation: `scanLine ${duration}s linear infinite`, pointerEvents: "none", zIndex: 2,
    }} />
  );
}

/* ─────────────────────────────────────────────────────────────────
   GLOBAL 3D KEYFRAMES  — inject once at app level
───────────────────────────────────────────────────────────────── */
export const GlobalFX = () => (
  <style>{`
    @keyframes glowOrb    { 0%,100%{opacity:.35} 50%{opacity:.8} }
    @keyframes floatY     { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
    @keyframes scanLine   { 0%{top:0%} 100%{top:105%} }
    @keyframes lineIn     { from{transform:scaleX(0)} to{transform:scaleX(1)} }
    @keyframes fadeUp     { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
    @keyframes fadeIn     { from{opacity:0} to{opacity:1} }
    @keyframes pulse3d    { 0%,100%{box-shadow:0 0 0 0 #4ade8055,0 4px 20px #00000060} 60%{box-shadow:0 0 0 10px #4ade8000,0 4px 20px #00000060} }
    @keyframes numGlow    { 0%,100%{text-shadow:0 0 16px #f5c51830} 50%{text-shadow:0 0 48px #f5c51870,0 0 80px #f5c51830} }
    @keyframes shimmer    { 0%,100%{opacity:0} 50%{opacity:1} }
    @keyframes rotateRing { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
    @keyframes dotBlink   { 0%,100%{opacity:1} 50%{opacity:0.25} }
    @keyframes cardFloat  { 0%,100%{transform:translateY(0) perspective(900px) rotateX(0deg)} 50%{transform:translateY(-6px) perspective(900px) rotateX(1.5deg)} }
    @keyframes borderGlow { 0%,100%{box-shadow:0 0 0 0 transparent} 50%{box-shadow:0 0 20px 1px #c9a84c22} }
    @keyframes navFadeDown { from{opacity:0;transform:translateY(-16px)} to{opacity:1;transform:translateY(0)} }
    @keyframes logoGlow   { 0%,100%{text-shadow:0 0 20px #c9a84c40} 50%{text-shadow:0 0 48px #f5c51870,0 0 80px #c9a84c30} }

    /* Smooth 3D hover for all .card elements */
    .card {
      transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease !important;
    }
    .card:hover {
      transform: translateY(-5px) perspective(800px) rotateX(1.5deg) !important;
      box-shadow: 0 24px 60px rgba(0,0,0,0.7), 0 0 40px rgba(201,168,76,0.08) !important;
    }

    /* Glowing border on focus */
    .form-input:focus, .form-textarea:focus, .form-select:focus {
      border-color: #c9a84c60 !important;
      box-shadow: 0 0 0 3px #c9a84c12, 0 0 20px #c9a84c10 !important;
      outline: none;
    }

    /* Scrollbar gold */
    ::-webkit-scrollbar-thumb:hover { background: #c9a84c80 !important; box-shadow: 0 0 8px #c9a84c40; }
  `}</style>
);
